import { useParams, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { FavoriteHeartButton } from '@/components/favorites/favorite-heart-button'
import { MarkdownContent } from '@/components/markdown/markdown-content'
import { ProjectHeroThumb } from '@/components/projects/project-hero-thumb'
import { VisibilityBadge } from '@/components/projects/visibility-badge'
import { AvatarImage } from '@/components/users/avatar-image'
import { useMyFavoritedIds } from '@/features/favorites/use-favorites'
import { useMe, usePublicProfile, useToggleSubscription } from '@/features/users/use-users'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import type { PublicProjectSummary } from '@/api/types'

const ProjectCard = ({
  project,
  authorHandle,
  isAuthenticated,
  isFavoritedByMe,
}: {
  project: PublicProjectSummary
  authorHandle: string
  isAuthenticated: boolean
  isFavoritedByMe: boolean
}) => (
  <Card className="relative">
    {isAuthenticated && (
      <div className="absolute right-2 top-2 z-10">
        <FavoriteHeartButton
          projectId={project.id}
          isFavoritedByMe={isFavoritedByMe}
          variant="overlay"
        />
      </div>
    )}
    <CardContent className="flex gap-3 p-4">
      <ProjectHeroThumb
        thumbUrl={project.heroThumbUrl ?? null}
        fallbackSeed={project.id}
        alt={`${project.title} hero`}
      />
      <div className="min-w-0 flex-1 space-y-1 pr-10">
        <div className="flex flex-wrap items-baseline gap-2">
          <Link
            to={canonicalProjectUrl(authorHandle, project.slug)}
            className="text-base font-medium hover:underline"
          >
            {project.title}
          </Link>
          {project.visibility === 'UNLISTED' && <VisibilityBadge visibility="UNLISTED" />}
        </div>
        {project.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">{project.description}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Updated {new Date(project.updatedAt).toLocaleDateString()}
        </p>
      </div>
    </CardContent>
  </Card>
)

export const ProfilePage = () => {
  const { handle } = useParams<{ handle: string }>()
  const { data: profile, isLoading, isError, error } = usePublicProfile(handle)
  const { data: me } = useMe()
  const toggle = useToggleSubscription(handle ?? '')
  const isAuthenticated = Boolean(me)
  const projectIds = profile?.publicProjects.map((p) => p.id) ?? []
  const { data: favoritedIds } = useMyFavoritedIds(projectIds, isAuthenticated)

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  if (isError) {
    const status = (error as { status?: number })?.status
    if (status === 404) {
      return (
        <div className="mx-auto max-w-3xl py-16 text-center">
          <h1 className="text-3xl font-bold">User not found</h1>
          <p className="mt-2 text-muted-foreground">No one with that handle.</p>
        </div>
      )
    }
    return <p className="text-destructive">Failed to load profile.</p>
  }

  if (!profile) return null

  const isSelf = me?.handle === profile.handle
  const isSubscribed = profile.isSubscribedByMe ?? false

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-start gap-4">
        <AvatarImage
          avatarUrl={profile.avatarUrl}
          handle={profile.handle}
          displayName={profile.displayName}
          size={96}
        />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-baseline gap-3">
            <h1 className="text-3xl font-bold tracking-tight">{profile.displayName}</h1>
            <span className="text-muted-foreground">@{profile.handle}</span>
          </div>
          {profile.bio && <MarkdownContent value={profile.bio} className="text-muted-foreground" />}
          <p className="text-xs text-muted-foreground">
            Joined {new Date(profile.joinedAt).toLocaleDateString()} · {profile.subscriberCount}{' '}
            subscriber{profile.subscriberCount === 1 ? '' : 's'}
          </p>
          {me && !isSelf && (
            <Button
              variant={isSubscribed ? 'outline' : 'default'}
              size="sm"
              onClick={() => toggle.mutate(!isSubscribed)}
              disabled={toggle.isPending}
            >
              {isSubscribed ? 'Subscribed' : 'Subscribe'}
            </Button>
          )}
          {isSelf && (
            <Button asChild size="sm" variant="outline">
              <Link to="/settings/profile">Edit profile</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">
          {isSelf ? 'Your projects' : 'Public projects'} ({profile.publicProjects.length})
        </h2>
        {profile.publicProjects.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">
            {isSelf ? 'No projects yet.' : 'No public projects yet.'}
          </p>
        ) : (
          <div className="space-y-2">
            {profile.publicProjects.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                authorHandle={profile.handle}
                isAuthenticated={isAuthenticated}
                isFavoritedByMe={favoritedIds?.has(p.id) ?? false}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
